
import { computed, defineComponent, ref, SetupContext } from 'vue';
import TIcon from './../t-icon/index';
import {
  CHECKED_ICON,
  UNCHECKED_ICON,
  CHECKED_DISABLED_ICON,
  UNCHECKED_DISABLED_ICON,
  CHECKED_CIRCLE_ICON,
  UNCHECKED_CIRCLE_ICON,
  CHECKED_CIRCLE_DISABLED_ICON,
  UNCHECKED_CIRCLE_DISABLED_ICON,
} from './../../typing/checkbox';

export default defineComponent({
  name: 'TCheckbox',
  components: { TIcon },
  props: {
    modelValue: {
      type: [Array, Boolean],
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    circle: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Object],
      default: '',
    },
    theme: {
      type: String,
      default: 'default',
      validator: (value: string) => ['default', 'primary', 'disabled'].includes(value),
    },
  },
  emits: ['click', 'update:modelValue'],
  setup(props, context: SetupContext) {
    const prefixCls = ref('t-checkbox');

    /** 组件的className前辍 */
    const getClasses = computed(() => {
      return {
        wrap: [prefixCls.value],
        control: [`${prefixCls.value}-control`],
        icon: [`${prefixCls.value}-icon`],
        content: [`${prefixCls.value}-content`],
        title: [`${prefixCls.value}-title`],
      };
    });

    // 判断checkbox是否被选中
    const getInnerChecked = computed(() => {
      if (typeof props.modelValue === 'boolean') {
        return props.modelValue;
      }
      return props.modelValue.includes(props.value);
    });

    // 获取图标的颜色配置
    const getIconTheme = computed(() => (props.disabled ? 'disabled' : props.theme));

    // 获取图标的外观形状
    const getIconShape = computed(() => {
      const defaultCheckIcon = props.circle ? CHECKED_CIRCLE_ICON : CHECKED_ICON;
      const defaultUnCheckIcon = props.circle ? UNCHECKED_CIRCLE_ICON : UNCHECKED_ICON;
      const disabledCheckIcon = props.circle ? CHECKED_CIRCLE_DISABLED_ICON : CHECKED_DISABLED_ICON;
      const disabledUnCheckIcon = props.circle ? UNCHECKED_CIRCLE_DISABLED_ICON : UNCHECKED_DISABLED_ICON;
      if (props.disabled) {
        return getInnerChecked.value ? disabledCheckIcon : disabledUnCheckIcon;
      }
      return getInnerChecked.value ? defaultCheckIcon : defaultUnCheckIcon;
    });

    // 存储一个是Array的modelValue，方便动态更新modelValue
    const getModelArrayValue = computed(() => (Array.isArray(props.modelValue) ? [...props.modelValue] : []));

    /** 对外响应的事件 */
    const listener = (event: any) => {
      if (props.disabled) {
        return;
      }
      if (typeof props.modelValue === 'boolean') {
        context.emit('update:modelValue', !props.modelValue);
      }
      if (Array.isArray(props.modelValue)) {
        const currentValues = getModelArrayValue.value;
        const index = currentValues.indexOf(props.value);
        if (index === -1) {
          currentValues.push(props.value);
        } else {
          currentValues.splice(index, 1);
        }
        context.emit('update:modelValue', currentValues);
      }
      context.emit('click', event);
    };

    return { prefixCls, getClasses, getInnerChecked, getIconTheme, getIconShape, getModelArrayValue, listener };
  },
});
